import React, {useEffect, useState} from 'react'
import {Autocomplete, TextField} from '@mui/material'
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import {getStationList, getCommodityList} from 'services/common.service'
import {fetchStateList} from 'redux/common.slice'
import {useDispatch} from 'react-redux'
import {type AppDispatch} from 'redux/store'
import _ from 'lodash'

const getErrorMsg = (id: string) => {
    switch (id) {
        case 'localAirport':
            return 'Local Airport is invalid.'
        case 'branchAirport':
            return 'Branch Airport is invalid.'
    }
}

export const accountNumberAutoCompleteCache = {}
export const nameAutoCompleteCache = {}

interface FormikAutocompleteProps {
    id: string
    className?: string
    formik: any
    renderOption?: any
    message?: string
    reflectField?: string
    type?: string
    onChange?(...args: unknown[]): unknown
    isAutoPopulated?: boolean
    disabled?: boolean
}

const FormikAutocomplete = ({
    id,
    formik,
    renderOption,
    message,
    className,
    reflectField,
    type,
    onChange,
    isAutoPopulated,
    disabled
}: FormikAutocompleteProps) => {
    const [options, setOptions] = useState<any[]>([])
    const [error, setError] = useState<any>('')
    const [isLoading, setLoading] = useState<boolean>(true)
    const [loadingText, setLoadingText] = useState<string | undefined>(message)
    let autoCompleteRef: any = null
    const dispatch = useDispatch<AppDispatch>()

    const handler = (event) => {
        if (type === 'airport' || type === undefined) {
            if (event.target.value.length === 3) {
                const result: any[] = options.filter((option) => option.code === event.target.value.toUpperCase())
                if (result.length > 0) {
                    formik.setFieldValue(id, result[0])
                    setError('')
                    formik.setFieldTouched(id, false)
                } else {
                    formik.setFieldValue(id, {code: event.target.value})
                    setError(getErrorMsg(id))
                    formik.setFieldTouched(id)
                }
                if (reflectField && reflectField.length > 0 && result.length > 0) {
                    const country = `${result[0].country} (${result[0].countryCode})`
                    formik.setFieldValue(reflectField, country)
                    const stateOrProvince = result[0].stateOrProvince
                    formik.setFieldValue('state', stateOrProvince)
                }
            } else if (event.target.value.length > 0) {
                const filteredOptions = options.filter((option) =>
                    option?.code?.toLowerCase().startsWith(event.target.value?.toLowerCase())
                )
                const sortedOptions = filteredOptions.sort((a, b) => a.code.localeCompare(b.code))
                if (sortedOptions.length > 0) {
                    formik.setFieldValue(id, sortedOptions[0])
                    setError('')
                    formik.setFieldTouched(id, false)
                    if (reflectField && reflectField.length > 0) {
                        const country = `${sortedOptions[0].country} (${sortedOptions[0].countryCode})`
                        formik.setFieldValue(reflectField, country)
                        const stateOrProvince = sortedOptions[0].stateOrProvince
                        formik.setFieldValue('state', stateOrProvince)
                    }
                } else {
                    formik.setFieldValue(id, {code: event.target.value})
                    setError(getErrorMsg(id))
                    formik.setFieldTouched(id)
                }
            }
        }
        if (event.target.value.length === 0) {
            formik.setFieldTouched(id)
            setError('')
        }
    }

    const keyHandler = (event) => {
        if (type === 'airport' || type === undefined) {
            const keyCode = event.keyCode || event.which
            const key = String.fromCharCode(keyCode)
            const alphabeticRegex = /[a-zA-Z\b\t\n]/
            if (!alphabeticRegex.test(key)) {
                event.preventDefault()
            }
        }
    }

    const changeHandler = (event) => {
        autoCompleteRef?.focus()
        if (event.target.value.length === 0) {
            formik.setFieldValue(id, '')
            formik.setFieldValue(reflectField, '')
            formik.setFieldValue('state', '')
        } else if (type === 'airport' || type === undefined) {
            formik.setFieldValue(id, {code: event.target.value})
        } else if (type === 'name' || type === 'account') {
            formik.setFieldValue(id, event.target.value)
        } else if (type === 'commodity') {
            formik.setFieldValue(id, event.target.value)
        }
    }

    const createFilterOptions = (inputValue: string, options: any[]) => {
        if (type === 'airport' || type === undefined) {
            const filteredOptions = options.filter((option) =>
                option?.code?.toLowerCase().startsWith(inputValue?.toLowerCase())
            )
            // Sort filtered options alphabetically by code
            return filteredOptions.sort((a, b) => a.code.localeCompare(b.code))
        } else if (type === 'account') {
            const updatedFilters = options.filter((item) =>
                item.accountNumber.toLowerCase().includes(inputValue.toLowerCase())
            )
            return updatedFilters
        } else if (type === 'name') {
            const updatedFilters = options.filter((item) => item.name.toLowerCase().includes(inputValue.toLowerCase()))
            return updatedFilters
        } else if (type === 'commodity') {
            const updatedFilters = options.filter((item) =>
                item?.description?.toLowerCase().includes(inputValue.toLowerCase())
            )
            return updatedFilters
        } else return []
    }

    const getOptions = async (value: string) => {
        if (type === 'airport' || type === undefined) {
            const data = await getStationList(value)

            setOptions(data.data.response)
            if (value.length === 3) {
                const result: any[] = data?.data?.response?.filter((option) => option.code === value.toUpperCase())
                if (result?.length === 0) {
                    setError(getErrorMsg(id))
                    formik.setFieldTouched(id)
                } else if (result?.length === 1) {
                    void dispatch(fetchStateList(result[0].countryCode))
                }
            } else if (value.length === 0) {
                setLoading(true)
                setLoadingText(message)
                setOptions([])
            } else {
                setLoading(false)
            }
            if (data?.data?.response?.length === 0) {
                formik.setFieldTouched(id)
                setLoading(true)
                setLoadingText('No Options')
                setError(getErrorMsg(id))
            } else {
                setError('')
            }
        } else if (type === 'commodity') {
            const data = await getCommodityList(value)
            const updatedOptions = data.data.response.map((item: Record<string, any>, index: number) => ({
                ...item,
                id: index
            }))
            setOptions(updatedOptions)
        }
    }

    useEffect(() => {
        if ((type === 'airport' || type === undefined) && _.get(formik.values, id)?.length > 0) {
            getStationList(_.get(formik.values, id))
                .then((data) => {
                    formik.setFieldValue(id, data?.data?.response[0])
                })
                .catch((err) => console.error(err))
        }
    }, [])

    const showHelperText = () => {
        if (_.get(formik.touched, id) && !!_.get(formik.errors, id)) {
            return (
                <>
                    <ErrorOutlineOutlinedIcon className='formik-error__info-icon' />
                    {error?.length > 0 ? error : _.get(formik.errors, id)}
                </>
            )
        } else return ''
    }

    const checkMaxLength = () => {
        if (type === 'airport' || type === undefined) {
            return 3
        } else if (type === 'account') {
            return 14
        } else if (type === 'name') {
            return 35
        } else {
            return 30
        }
    }

    return (
        <Autocomplete
            freeSolo
            autoHighlight
            autoComplete
            disableClearable
            disabled={disabled}
            id={id}
            loadingText={loadingText}
            loading={isLoading}
            options={options}
            getOptionLabel={(option) => {
                if (type === 'airport' || type === undefined) return option?.code ?? option
                else if (type === 'account') return option?.accountNumber ?? option
                else if (type === 'name') return option?.name ?? option
                else if (type === 'commodity') return option?.code ?? option
                else return option
            }}
            value={_.get(formik.values, id)}
            filterOptions={(options, {inputValue}) => createFilterOptions(inputValue, options)}
            className={className}
            renderOption={renderOption}
            onChange={(event, value) => {
                if (value) {
                    if (type === 'airport' || type === undefined) formik.setFieldValue(id, value)
                    if (reflectField && reflectField.length > 0 && (type === 'airport' || type === undefined)) {
                        formik.setFieldValue(reflectField, value.country)
                    }
                    if (type === 'commodity') {
                        formik.setFieldValue(id, `${value.code}`)
                        void formik.setFieldValue('description', value.description)
                        void formik.setFieldValue('bookingRemarks', value.code)
                    }
                    if (onChange) onChange(event, value)
                }
            }}
            onInputChange={(_, value) => {
                void getOptions(value)
            }}
            isOptionEqualToValue={(option, value) => {
                if (type === 'airport' || type === undefined) return option.code === value.code
                else if (type === 'account') return option.accountNumber === value.accountNumber
                else if (type === 'name') return option.name === value.name
                else if (type === 'commodity') return option.code === value.code
                return false
            }}
            renderInput={(params) => (
                <>
                    <TextField
                        {...params}
                        variant='outlined'
                        ref={autoCompleteRef}
                        value={_.get(formik.values, id)}
                        onBlur={handler}
                        onChange={changeHandler}
                        onKeyDown={keyHandler}
                        InputProps={{
                            ...params.InputProps,
                            inputProps: {
                                ...params.inputProps,
                                id,
                                maxLength: checkMaxLength()
                            }
                        }}
                        inputRef={(i) => {
                            autoCompleteRef = i
                        }}
                        error={error?.length > 0 || (_.get(formik.touched, id) && !!_.get(formik.errors, id))}
                        helperText={showHelperText()}
                    />
                </>
            )}
        />
    )
}

FormikAutocomplete.defaultProps = {
    type: 'airport',
    isAutoPopulated: false
}

export default FormikAutocomplete
